import React, { useEffect, useState, useRef } from 'react';
import { XIcon, CalendarIcon, CheckCircleIcon } from 'lucide-react';
import type { Loan } from './LoanRow';
interface MarkCompleteModalProps {
  loan: Loan;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (loanId: string, note: string, followUpDate: string) => void;
}
export function MarkCompleteModal({
  loan,
  isOpen,
  onClose,
  onConfirm
}: MarkCompleteModalProps) {
  const [note, setNote] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [dateError, setDateError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (isOpen) {
      setNote('');
      setFollowUpDate('');
      setDateError('');
      // Focus the first input when modal opens
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
      // Trap focus within modal
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>('button, textarea, input, [tabindex]:not([tabindex="-1"])');
        const firstEl = focusableElements[0];
        const lastEl = focusableElements[focusableElements.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  function handleSubmit() {
    if (!followUpDate) {
      setDateError('Please select a follow-up date');
      return;
    }
    setDateError('');
    onConfirm(loan.id, note, followUpDate);
  }
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-79">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-80" />

      {/* Modal */}
      <div ref={modalRef} className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-81">
        {/* Header */}
        <div className="px-6 pt-6 pb-4" data-id="element-82">
          <div className="flex items-start justify-between" data-id="element-83">
            <div data-id="element-84">
              <h2 id="modal-title" className="text-lg font-semibold text-gray-900" data-id="element-85">
                Complete Follow-Up
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-86">
                Add notes and schedule the next follow-up for{' '}
                <span className="font-medium text-gray-700" data-id="element-87">
                  {loan.borrowerName}
                </span>
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label="Close modal" data-id="element-88">
              <XIcon className="w-5 h-5" data-id="element-89" />
            </button>
          </div>
        </div>

        <div className="h-px bg-gray-100" data-id="element-90" />

        {/* Body */}
        <div className="px-6 py-5 space-y-5" data-id="element-91">
          {/* Loan info summary */}
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg" data-id="element-92">
            <div className="text-xs text-gray-500" data-id="element-93">
              <span className="font-medium text-gray-700" data-id="element-94">
                Loan #{loan.loanNumber}
              </span>
              <span className="mx-2" data-id="element-95">·</span>
              <span data-id="element-96">{loan.loanType}</span>
              <span className="mx-2" data-id="element-97">·</span>
              <span data-id="element-98">{loan.rate}</span>
              <span className="mx-2" data-id="element-99">·</span>
              <span data-id="element-100">Funded {loan.fundedDate}</span>
            </div>
          </div>

          {/* Follow Up Note */}
          <div data-id="element-101">
            <label htmlFor="follow-up-note" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-102">
              Follow-Up Note
            </label>
            <textarea ref={firstInputRef} id="follow-up-note" value={note} onChange={e => setNote(e.target.value)} placeholder="Add notes about this follow-up..." rows={4} className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all placeholder:text-gray-400" data-id="element-103" />
          </div>

          {/* Next Follow Up Date */}
          <div data-id="element-104">
            <label htmlFor="follow-up-date" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-105">
              Next Follow-Up Date <span className="text-red-500" data-id="element-106">*</span>
            </label>
            <div className="relative" data-id="element-107">
              <input id="follow-up-date" type="date" value={followUpDate} onChange={e => {
              setFollowUpDate(e.target.value);
              if (dateError) setDateError('');
            }} className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${dateError ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'}`} aria-invalid={!!dateError} aria-describedby={dateError ? 'date-error' : undefined} data-id="element-108" />
            </div>
            {dateError && <p id="date-error" className="mt-1.5 text-xs text-red-600" role="alert" data-id="element-109">
                {dateError}
              </p>}
          </div>
        </div>

        <div className="h-px bg-gray-100" data-id="element-110" />

        {/* Footer */}
        <div className="px-6 py-4 flex items-center justify-end gap-3 bg-gray-50/50" data-id="element-111">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2" data-id="element-112">
            Cancel
          </button>
          <button onClick={handleSubmit} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2" data-id="element-113">
            <CheckCircleIcon className="w-4 h-4" data-id="element-114" />
            Mark Complete
          </button>
        </div>
      </div>
    </div>;
}